import { Avatar } from '@startuptools/ui/Avatar';
import { Button } from '@startuptools/ui/Button';
import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Typography } from '@startuptools/ui/Typography';
import { IconButton } from '@startuptools/ui/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoveUpIcon from '@mui/icons-material/MoveUp';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import { Trans } from '@lingui/react';
import { RichUserText } from '@startuptools/ui/RichUserText';
import { Alert } from '@startuptools/ui/Alert';
import {
  GqlAgendaItem,
  GqlAgendaItemDecision,
  GqlMeeting,
  GqlMeetingParticipant,
} from '../../../graphql/base-types.graphql';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { GqlDeleteAiMutationFn, GqlSetAiOrderMutationFn } from '../../../graphql/react-operations';
import { canMove } from './edit-steps/utils';
import React, { Dispatch, SetStateAction, useRef } from 'react';
import { AgendaItemEdit } from './AgendaItemEdit';
import { AiMcProps } from './agenda-items/MeetingConcern';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { ProtocolEditProvider } from './ProtocolEditContext';
import { CircularProgress } from '@startuptools/ui/joy/CircularProgress';

export const ProtocolEditItem = ({
  meeting,
  ai,
  meetingConcerns,
  agendaItems,
  isBoardsRecommendation,
  movableRange,
  editAiId,
  setEditAiId,
  loading,
  isEditingAgendaItem,
  doMove,
  doDelete,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'primaryLang' | 'secondaryLang' | 'companyId' | 'startTime' | 'kind'> & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
  ai: Pick<
    GqlAgendaItem,
    | 'id'
    | 'subjectPrimaryLang'
    | 'subjectSecondaryLang'
    | 'order'
    | 'done'
    | 'canHaveBoardsRecommendation'
    | 'commentPrimaryLang'
    | 'commentSecondaryLang'
    | 'mandatory'
    | 'kind'
    | 'firstGroup'
    | 'lastGroup'
  > & {
    agendaItemDecisions: Pick<
      GqlAgendaItemDecision,
      'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
    >[];
  };
  meetingConcerns: AiMcProps[];
  agendaItems: Array<
    Pick<
      GqlAgendaItem,
      | 'id'
      | 'subjectPrimaryLang'
      | 'subjectSecondaryLang'
      | 'order'
      | 'done'
      | 'canHaveBoardsRecommendation'
      | 'commentPrimaryLang'
      | 'commentSecondaryLang'
      | 'mandatory'
      | 'kind'
      | 'firstGroup'
      | 'lastGroup'
    > & {
      agendaItemDecisions: Pick<
        GqlAgendaItemDecision,
        'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
      >[];
    }
  >;
  isBoardsRecommendation?: boolean;
  movableRange: number[];
  editAiId: string | null;
  setEditAiId: Dispatch<SetStateAction<string | null>>;
  loading: boolean;
  isEditingAgendaItem: boolean;
  doMove: GqlSetAiOrderMutationFn;
  doDelete: GqlDeleteAiMutationFn;
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const move = canMove(movableRange, ai.order);
  const [itemLoading, setItemLoading] = React.useState(false);

  const handleEdit = async (id: string) => {
    setEditAiId(id);
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  return (
    <Card
      key={ai.id}
      ref={scrollRef}
      sx={theme => ({ borderColor: editAiId === ai.id ? theme.palette.primary[400] : undefined })}
    >
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" gap={2} alignItems="center">
            {itemLoading ? (
              <CircularProgress size="sm" sx={{ width: '32px', height: '32px' }} />
            ) : (
              <Avatar size="sm" variant="solid" color={ai.done ? 'success' : 'primary'}>
                {ai.order}
              </Avatar>
            )}
            <Stack>
              {meeting.primaryLang && <Typography level="title-md">{ai.subjectPrimaryLang}</Typography>}
              {meeting.secondaryLang && (
                <Typography level="title-md" component="i">
                  {ai.subjectSecondaryLang}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" gap={1}>
            {!ai.done && isBoardsRecommendation && ai.canHaveBoardsRecommendation && (
              <>
                <Box>
                  <Alert variant="outlined" color="danger" size="sm">
                    <Trans id="A board's recommendation must be set" />
                  </Alert>
                </Box>
                <Box>
                  <Button onClick={() => handleEdit(ai.id)} disabled={isEditingAgendaItem}>
                    {ai.canHaveBoardsRecommendation ? (
                      <Trans id="Set Board's Recommendation" />
                    ) : (
                      <Trans id="Make Decision" />
                    )}
                  </Button>
                </Box>
              </>
            )}

            {!ai.done && !isBoardsRecommendation && (
              <Box>
                <Button onClick={() => handleEdit(ai.id)} disabled={isEditingAgendaItem}>
                  <Trans id="Make Decision" />
                </Button>
              </Box>
            )}

            <Box>
              <ButtonGroup>
                {ai.done && (
                  <IconButton variant="outlined" onClick={() => handleEdit(ai.id)} disabled={isEditingAgendaItem}>
                    <EditIcon />
                  </IconButton>
                )}
                {move && (
                  <>
                    <IconButton
                      onClick={() =>
                        doMove({
                          variables: { companyId: meeting.companyId, input: { id: ai.id, order: ai.order - 1 } },
                        })
                      }
                      disabled={!move.up || loading || isEditingAgendaItem}
                    >
                      <MoveUpIcon />
                    </IconButton>
                    <IconButton
                      onClick={() =>
                        doMove({
                          variables: { companyId: meeting.companyId, input: { id: ai.id, order: ai.order + 1 } },
                        })
                      }
                      disabled={!move.down || loading || isEditingAgendaItem}
                    >
                      <MoveDownIcon />
                    </IconButton>
                  </>
                )}

                {!ai.mandatory ? (
                  <IconButton
                    color="danger"
                    onClick={() => doDelete({ variables: { companyId: meeting.companyId, id: ai.id } })}
                    disabled={isEditingAgendaItem}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : (
                  <Tooltip title={<Trans id="Mandatory Agenda Item, can not be deleted" />} disableInteractive={false}>
                    <IconButton sx={theme => ({ color: theme.palette.neutral[300] })}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ButtonGroup>
            </Box>
          </Stack>
        </Stack>
        {ai.done && editAiId !== ai.id && (
          <Stack gap={1}>
            {ai.agendaItemDecisions.map(aid => (
              <Card key={aid.id}>
                <Typography component="span">
                  Beslut: <RichUserText>{aid.decisionPrimaryLang}</RichUserText>
                </Typography>
                {meeting.secondaryLang && (
                  <Typography component="i">
                    Decision: <RichUserText>{aid.decisionSecondaryLang}</RichUserText>
                  </Typography>
                )}
              </Card>
            ))}
            {((meeting.primaryLang && ai.commentPrimaryLang) || (meeting.secondaryLang && ai.commentSecondaryLang)) && (
              <Card>
                {meeting.primaryLang && ai.commentPrimaryLang && (
                  <Typography component="span">
                    Kommentar: <RichUserText>{ai.commentPrimaryLang}</RichUserText>
                  </Typography>
                )}
                {meeting.secondaryLang && ai.commentSecondaryLang && (
                  <Typography component="i">
                    Comment: <RichUserText>{ai.commentSecondaryLang}</RichUserText>
                  </Typography>
                )}
              </Card>
            )}
          </Stack>
        )}
        {ai.id === editAiId && (
          <ProtocolEditProvider loading={itemLoading} setLoading={setItemLoading}>
            <AgendaItemEdit
              meeting={meeting}
              meetingConcerns={meetingConcerns}
              agendaItems={agendaItems}
              agendaItem={ai}
              onClose={() => setEditAiId(null)}
              summonsDirty={isBoardsRecommendation ?? false}
            />
          </ProtocolEditProvider>
        )}
      </Stack>
    </Card>
  );
};
